import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { FAQItem } from 'components/molecules';
import { Section } from 'components/core/Page';
import { H2 } from 'components/core/Typography';
import './FacilityAmenitiesAccordion.scss';

export const FeaturesAmenitiesAccordion = (props) => {
	// Use featuresDataByCategory if available, otherwise fall back to empty structure
	const featuresDataByCategory = props.facility?.featuresDataByCategory || {};

	// Helper function to check if a category has attributes using featuresDataByCategory structure
	const categoryHasAttributes = (categoryKey) => {
		return featuresDataByCategory[categoryKey]?.hasAttributes === true;
	};

	// Helper function to create feature content sections
	const createFeatureContent = (categories) => {
		return categories
			.filter(({ key }) => categoryHasAttributes(key))
			.map(({ key, title }) => ({
				title,
				content: featuresDataByCategory[key]?.items || [],
			}));
	};
	// Helper function to safely decode and format description for markdown rendering
	const getFormattedDescription = (description) => {
		if (!description) return 'Facility description not available.';

		const formatText = (text) => {
			return text
				.replace(/<br\s*\/?>\s*<br\s*\/?>/gi, '\n\n') // Convert double br tags to double newlines
				.replace(/<br\s*\/?>/gi, '\n\n') // Convert single br tags to double newlines
				.replace(/\n+/g, '\n\n') // Normalize multiple newlines to double newlines
				.trim(); // Remove leading/trailing whitespace
		};

		try {
			return formatText(decodeURIComponent(description));
		} catch (error) {
			// If decodeURIComponent fails, use the original description
			return formatText(description);
		}
	};

	// Facility Description Accordian Content
	const feature0 = useMemo(
		() => ({
			content: [
				{
					question: 'Facility Description',
					answer: `**About ${props.facility?.name}**\n\n${getFormattedDescription(props.facility?.description)}`,
				},
			],
		}),
		[props.facility?.name, props.facility?.description],
	);

	const feature1 = useMemo(
		() => ({
			content: [
				{
					question: 'Office & Access Hours',
					answer: {
						accessHours: props.facility?.hours?.access,
						officeHours: props.facility?.hours?.office,
					},
				},
			],
		}),
		[props.facility?.hours],
	);

	// Create feature2 only if either access or security has true attributes
	const feature2 = useMemo(() => {
		const feature2Content = createFeatureContent([
			{ key: 'access', title: 'Access' },
			{ key: 'security', title: 'Security' },
		]);
		return feature2Content.length > 0
			? {
					content: [
						{
							question: 'Accessibility & Security',
							answer: feature2Content,
						},
					],
				}
			: null;
	}, [featuresDataByCategory.access, featuresDataByCategory.security]);

	// Create feature3 only if either vehicle requirements or property coverage has true attributes
	const feature3 = useMemo(() => {
		const feature3Content = createFeatureContent([
			{
				key: 'vehicleStorageRequirements',
				title: 'Vehicle Storage Requirements',
			},
			{ key: 'propertyCoverage', title: 'Property Coverage' },
		]);
		return feature3Content.length > 0
			? {
					content: [
						{
							question: 'Vehicle Storage & Property Coverage',
							answer: feature3Content,
						},
					],
				}
			: null;
	}, [
		featuresDataByCategory.vehicleStorageRequirements,
		featuresDataByCategory.propertyCoverage,
	]);

	// Create feature4 only if any of payments, billing, or miscellaneous has true attributes
	const feature4 = useMemo(() => {
		const feature4Content = createFeatureContent([
			{ key: 'payments', title: 'Payments' },
			{ key: 'billing', title: 'Billing' },
			{ key: 'miscellaneous', title: 'Miscellaneous' },
		]);
		return feature4Content.length > 0
			? {
					content: [
						{
							question: 'Payments, Billing and Miscellaneous',
							answer: feature4Content,
						},
					],
				}
			: null;
	}, [
		featuresDataByCategory.payments,
		featuresDataByCategory.billing,
		featuresDataByCategory.miscellaneous,
	]);

	// Create feature5 only if premium services has any true attributes
	const feature5 = useMemo(
		() =>
			categoryHasAttributes('premiumServices')
				? {
						content: [
							{
								question: 'Premium Services',
								answer: [
									{
										content:
											featuresDataByCategory
												.premiumServices?.items || [],
									},
								],
							},
						],
					}
				: null,
		[featuresDataByCategory.premiumServices],
	);

	// Safety check - don't render anything if no facility data
	if (!props.facility) {
		return null;
	}

	return (
		<Section className="facility-amenities-accordion-wrapper">
			<Section className="facility-amenities-accordion-header">
				<H2
					size="large"
					weight="extraBold"
					last
				>
					{props.title || 'Facility Amenities & Features'}
				</H2>
			</Section>
			<Section className="facility-amenities-accordion">
				{props.facility.description && (
					<FAQItem
						{...feature0}
						iconType="expand"
					/>
				)}
				<FAQItem
					{...feature1}
					type="accessHours"
					iconType="expand"
				/>
				{feature2 && (
					<FAQItem
						{...feature2}
						type="facility"
						iconType="expand"
					/>
				)}
				{feature3 && (
					<FAQItem
						{...feature3}
						type="facility"
						iconType="expand"
					/>
				)}
				{feature4 && (
					<FAQItem
						{...feature4}
						type="facility"
						iconType="expand"
					/>
				)}
				{feature5 && (
					<FAQItem
						{...feature5}
						type="facility"
						iconType="expand"
					/>
				)}
			</Section>
		</Section>
	);
};

FeaturesAmenitiesAccordion.propTypes = {
	facility: PropTypes.shape({
		featuresDataByCategory: PropTypes.object,
		groupedAmenities: PropTypes.object,
		name: PropTypes.string,
		description: PropTypes.string,
		hours: PropTypes.shape({
			access: PropTypes.shape({
				text: PropTypes.string,
			}),
			office: PropTypes.shape({
				text: PropTypes.string,
			}),
		}),
	}).isRequired,
	title: PropTypes.string,
};

export default FeaturesAmenitiesAccordion;
